import { useMemo } from 'preact/hooks';
import { ACCOUNT_KIND_ORDER, acctKey, ledger, type Account, type Entry } from '@khata/core';
import { taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, state } from '../lib/khata.js';
import { accountKind } from '../lib/labels.js';
import { Picker } from './Picker.js';

/** Usage is counted over the most recent entries only, so a wallet that fell out of use sinks. */
const RECENT = 600;

function usage(all: ReadonlyArray<Entry>): ReadonlyMap<string, number> {
  const n = new Map<string, number>();
  const bump = (id: string) => n.set(id, (n.get(id) ?? 0) + 1);
  for (let i = all.length - 1, seen = 0; i >= 0 && seen < RECENT; i--, seen++) {
    const e = all[i]!;
    if (e.type === 'transfer') {
      bump(e.fromAccountId);
      bump(e.toAccountId);
    } else if ('accountId' in e) bump(e.accountId);
  }
  return n;
}

/**
 * Choose where money goes or comes from, the same way a party or item is chosen: the current
 * account as a row (name, kind, balance) with Change, then a list of every account with the
 * most used ones first and type-to-search for the rest. Works the same with three accounts
 * or thirty.
 */
export function AccountPicker(props: {
  value: string;
  onChange: (id: string) => void;
  accounts: ReadonlyArray<Account>;
  invalid?: boolean;
  testId?: string;
}) {
  const s = state.value;
  const recent = entries.value;
  const used = useMemo(() => usage(recent), [recent]);
  const options = useMemo(
    () =>
      [...props.accounts]
        .sort(
          (a, b) =>
            (used.get(b.id) ?? 0) - (used.get(a.id) ?? 0) ||
            ACCOUNT_KIND_ORDER[a.kind] - ACCOUNT_KIND_ORDER[b.kind],
        )
        .map((a) => ({
          id: a.id,
          label: a.name,
          sub: accountKind(a.kind),
          right: taka(ledger(s, acctKey(a.id))),
        })),
    [props.accounts, used, s],
  );
  return (
    <Picker
      value={props.value}
      onChange={props.onChange}
      options={options}
      placeholder={t('picker_account')}
      invalid={props.invalid}
      testId={props.testId}
    />
  );
}
